import dayjs, { Dayjs } from "dayjs";
import React, { useEffect } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex } from "antd";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  IContestTerritory,
  IContestTerritoryPayload,
  useSaveContestTerritory,
} from "~/entity/ContestTerritories";
import { useGetLocationData } from "~/hooks/locationData";
import { useSubjectOptions } from "~/pages/Editor/ContestTerritories/hooks/useSubjectOptions";
import { OpenModal } from "~/pages/Editor/type";
import { useNotify } from "~/shared/hooks/useNotify";
import { filterOptionByLabel } from "~/shared/lib/filterHelpers";
import { Field } from "~/shared/ui/Field";

interface IContestTerritoryFormValues {
  geoPlaceId: string;
  name: string;
  startDate: Dayjs;
  endDate?: Dayjs | null;
}

interface ContestTerritoryFormProps {
  refetch: () => void;
  closeModal: () => void;
  currentData?: IContestTerritory[];
  formId: string;
}

const ContestTerritoryForm = ({
  refetch,
  closeModal,
  currentData,
  formId,
}: ContestTerritoryFormProps) => {
  const { t } = useTranslation();
  const { save } = useSaveContestTerritory();
  const { notifyError, notifySuccess } = useNotify();
  const { currentId, openModal } = useGetLocationData();
  const { options, loading: loadingRegions } = useSubjectOptions();
  const isEdit = openModal === OpenModal.Edit;

  const currentTerritory = isEdit
    ? currentData?.find((item) => item.id === currentId)
    : undefined;

  const schema = yup.object().shape({
    geoPlaceId: yup.string().required(t("validation.required")),
    name: yup.string().max(255, t("validation.more255")),
    startDate: yup.mixed().required(t("validation.required")),
    endDate: yup
      .mixed()
      .nullable()
      .test(
        "endBeforeStart",
        t("contestTerritories.validation.endBeforeStart"),
        function (value) {
          const start = this.parent.startDate;
          if (!value || !start) return true;
          return !dayjs(value as Dayjs).isBefore(dayjs(start as Dayjs), "day");
        },
      ),
  });

  const getDefaultValues = () => ({
    geoPlaceId:
      currentTerritory?.geoPlaceId != null
        ? String(currentTerritory.geoPlaceId)
        : undefined,
    name: currentTerritory?.name ?? "",
    startDate: currentTerritory?.startDate
      ? dayjs(currentTerritory.startDate, "YYYY-MM-DD")
      : undefined,
    endDate: currentTerritory?.endDate
      ? dayjs(currentTerritory.endDate, "YYYY-MM-DD")
      : null,
  });

  const { control, handleSubmit, reset, errors } =
    useForm<IContestTerritoryFormValues>({
      defaultValues: getDefaultValues(),
      resolver: yupResolver(schema),
    });

  useEffect(() => {
    reset(getDefaultValues());
  }, [currentTerritory, reset]);

  const onError = (error: any) => {
    notifyError(error.message);
  };

  const onSubmit = (prop: IContestTerritoryFormValues) => {
    const region = options.find((option) => option.value === prop.geoPlaceId);
    const payload: IContestTerritoryPayload = {
      geoPlaceId: Number(prop.geoPlaceId),
      regionName: region?.label ?? currentTerritory?.regionName ?? "",
      name: prop.name || null,
      startDate: dayjs(prop.startDate).format("YYYY-MM-DD"),
      endDate: prop.endDate ? dayjs(prop.endDate).format("YYYY-MM-DD") : null,
    };
    save(
      payload,
      {
        onSuccess: () => {
          notifySuccess(
            t(
              isEdit
                ? "contestTerritories.editedTerritory"
                : "contestTerritories.createdTerritory",
            ),
          );
          refetch();
          closeModal();
        },
        onError,
      },
      isEdit ? currentId : undefined,
    );
  };

  return (
    <form id={formId} onSubmit={handleSubmit(onSubmit)}>
      <Flex vertical gap={10}>
        <Field
          type="select"
          name="geoPlaceId"
          control={control}
          label={t("contestTerritories.form.region")}
          options={options}
          loading={loadingRegions}
          showSearch
          filterOption={filterOptionByLabel}
          error={errors["geoPlaceId"]?.message}
        />
        <Field
          type="input"
          name="name"
          control={control}
          label={t("contestTerritories.form.name")}
          error={errors["name"]?.message}
        />
        <Field
          type="datePicker"
          name="startDate"
          control={control}
          label={t("contestTerritories.form.startDate")}
          format="DD.MM.YYYY"
          error={(errors["startDate"] as any)?.message}
        />
        <Field
          type="datePicker"
          name="endDate"
          control={control}
          label={t("contestTerritories.form.endDate")}
          format="DD.MM.YYYY"
          error={(errors["endDate"] as any)?.message}
        />
      </Flex>
    </form>
  );
};

export default ContestTerritoryForm;
